import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { ArrowUpRight, CalendarDays, Loader2 } from "lucide-react";
import { track } from "@/lib/analytics";
import { getAttribution } from "@/lib/utm";
import { CAP_PAGES } from "@/data/images";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_SLOTS = [
  "09:00 AM",
  "10:00 AM",
  "11:00 AM",
  "12:00 PM",
  "02:00 PM",
  "03:00 PM",
  "04:00 PM",
  "05:00 PM",
  "06:00 PM",
];

const NOT_SURE = "Not sure yet";
const SERVICE_OPTIONS = [
  ...Object.values(CAP_PAGES).flat().map((p) => p.title),
  NOT_SURE,
];

const initialForm = {
  name: "",
  email: "",
  phone: "",
  company: "",
  message: "",
  website: "", // honeypot — must stay empty
};

/**
 * Reusable lead-capture dialog used sitewide.
 * - Service preselected from the page via the `service` prop; user can change it.
 * - Auto-captures source page, referrer and first-touch UTM parameters.
 * - Honeypot spam protection; input preserved on failure.
 */
export default function BookConsultationDialog({ open, onOpenChange, service }) {
  const [form, setForm] = useState(initialForm);
  const [serviceInterest, setServiceInterest] = useState(NOT_SURE);
  const [dateStr, setDateStr] = useState(""); // ISO yyyy-mm-dd
  const [time, setTime] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Preselect the page's service each time the dialog opens.
  useEffect(() => {
    if (open) {
      setServiceInterest(
        service && SERVICE_OPTIONS.includes(service) ? service : NOT_SURE
      );
    }
  }, [open, service]);

  const todayIso = new Date().toISOString().slice(0, 10);

  const setField = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const reset = () => {
    setForm(initialForm);
    setDateStr("");
    setTime("");
  };

  const handleClose = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.company.trim() || !form.message.trim()) {
      toast.error("Please fill in name, company, work email, and a short message.");
      return;
    }
    setSubmitting(true);
    const attr = getAttribution();
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        company: form.company.trim(),
        message: form.message.trim(),
        preferred_date: dateStr ? format(parseISO(dateStr), "EEE, dd MMM yyyy") : null,
        preferred_time: time || null,
        service: serviceInterest,
        source_page: window.location.pathname,
        referrer: attr.referrer || null,
        utm_source: attr.utm_source || null,
        utm_medium: attr.utm_medium || null,
        utm_campaign: attr.utm_campaign || null,
        utm_content: attr.utm_content || null,
        utm_term: attr.utm_term || null,
        website: form.website || null, // honeypot
      };
      await axios.post(`${API}/contact`, payload);
      // Conversion events fire only after backend success. No PII is sent.
      track("generate_lead", {
        service: serviceInterest,
        source_page: window.location.pathname,
        form: "consultation_dialog",
      });
      track("consultation_request", {
        service: serviceInterest,
        has_date: !!payload.preferred_date,
        has_time: !!payload.preferred_time,
      });
      toast.success("Thank you for contacting BLEZZINGS.", {
        description: "We have received your enquiry and will be in touch.",
      });
      reset();
      onOpenChange(false);
    } catch (err) {
      console.error(err);
      const detail =
        err?.response?.data?.detail || "Something went wrong. Please try again — your input is preserved.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid="book-dialog"
        className="max-w-2xl rounded-none border-zinc-200 p-0 gap-0 bg-white max-h-[92dvh] overflow-y-auto overscroll-contain"
      >
        <div className="px-8 pt-8 pb-4 border-b border-zinc-200">
          <DialogHeader className="text-left space-y-2">
            <div className="text-[10px] uppercase tracking-[0.28em] text-black font-semibold">
              Enquiry
            </div>
            <DialogTitle className="font-serif-display text-3xl sm:text-4xl leading-tight font-normal">
              Tell Us About Your Growth Challenge
            </DialogTitle>
            <DialogDescription className="text-zinc-600 text-sm">
              Share your business objective or challenge. Our team will review
              your enquiry and get in touch to explore how we can help.
            </DialogDescription>
          </DialogHeader>
        </div>

        <form onSubmit={submit} className="px-8 py-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Full name*" htmlFor="bk-name">
              <Input
                id="bk-name"
                data-testid="bk-name"
                value={form.name}
                onChange={setField("name")}
                required
                placeholder="Full name"
                className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-black h-11"
              />
            </Field>
            <Field label="Company name*" htmlFor="bk-company">
              <Input
                id="bk-company"
                data-testid="bk-company"
                value={form.company}
                onChange={setField("company")}
                required
                placeholder="Company"
                className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-black h-11"
              />
            </Field>
            <Field label="Work email*" htmlFor="bk-email">
              <Input
                id="bk-email"
                data-testid="bk-email"
                type="email"
                value={form.email}
                onChange={setField("email")}
                required
                placeholder="you@company.com"
                className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-black h-11"
              />
            </Field>
            <Field label="Phone" htmlFor="bk-phone">
              <Input
                id="bk-phone"
                data-testid="bk-phone"
                value={form.phone}
                onChange={setField("phone")}
                placeholder="+91 ..."
                className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-black h-11"
              />
            </Field>
          </div>

          <Field label="Service required*" htmlFor="bk-service">
            <Select value={serviceInterest} onValueChange={setServiceInterest}>
              <SelectTrigger
                id="bk-service"
                data-testid="bk-service-trigger"
                className="rounded-none border-zinc-300 h-11 focus:ring-0 focus:border-black"
              >
                <SelectValue placeholder="Select a service" />
              </SelectTrigger>
              <SelectContent className="rounded-none max-h-72">
                {SERVICE_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`bk-service-${s}`}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Preferred date" htmlFor="bk-date">
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    id="bk-date"
                    data-testid="bk-date"
                    className="flex h-11 w-full items-center justify-between border border-zinc-300 bg-transparent px-3 text-left text-sm transition-colors hover:border-black"
                  >
                    <span className={dateStr ? "text-black" : "text-zinc-400"}>
                      {dateStr ? format(parseISO(dateStr), "EEE, dd MMM yyyy") : "Pick a preferred date"}
                    </span>
                    <CalendarDays className="h-4 w-4 text-zinc-400" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 rounded-none border-zinc-300" align="start">
                  <Calendar
                    mode="single"
                    selected={dateStr ? parseISO(dateStr) : undefined}
                    onSelect={(d) => d && setDateStr(format(d, "yyyy-MM-dd"))}
                    disabled={{ before: new Date(todayIso) }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </Field>

            <Field label="Preferred time" htmlFor="bk-time">
              <Select value={time} onValueChange={setTime}>
                <SelectTrigger
                  id="bk-time"
                  data-testid="bk-time-trigger"
                  className="rounded-none border-zinc-300 h-11 focus:ring-0 focus:border-black"
                >
                  <SelectValue placeholder="Pick a time slot" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {TIME_SLOTS.map((slot) => (
                    <SelectItem key={slot} value={slot} data-testid={`bk-time-${slot}`}>
                      {slot} IST
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="Business challenge or objective*" htmlFor="bk-message">
            <Textarea
              id="bk-message"
              data-testid="bk-message"
              value={form.message}
              onChange={setField("message")}
              required
              rows={4}
              placeholder="Briefly describe the challenge you're solving for or the opportunity you're chasing — and the outcome you'd like."
              className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-black resize-none"
            />
          </Field>

          {/* Honeypot — invisible to humans, irresistible to bots */}
          <div aria-hidden="true" className="absolute -left-[9999px] -top-[9999px] h-0 w-0 overflow-hidden">
            <label htmlFor="bk-website">Website</label>
            <input
              id="bk-website"
              data-testid="bk-website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={form.website}
              onChange={setField("website")}
            />
          </div>

          <DialogFooter className="px-0 pt-2 sm:justify-between gap-3">
            <p className="text-xs text-zinc-500">
              Your details stay private — see our{" "}
              <a href="/privacy-policy" className="underline underline-offset-2 hover:text-black">
                Privacy Policy
              </a>
              .
            </p>
            <button
              data-testid="bk-submit"
              type="submit"
              disabled={submitting}
              className="group inline-flex items-center justify-center gap-2 h-11 px-6 bg-black text-white text-sm font-medium hover:bg-zinc-800 transition-colors duration-300 disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending
                </>
              ) : (
                <>
                  Discuss My Business Challenge
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </>
              )}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, htmlFor, children }) {
  return (
    <div className="space-y-1.5">
      <Label
        htmlFor={htmlFor}
        className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold"
      >
        {label}
      </Label>
      {children}
    </div>
  );
}
